/**
 * Note names spelled for a key: in E major the note between G and A is G♯,
 * in A♭ major it is A♭. Keys are given as key signatures (fifths: sharps
 * above zero, flats below).
 */

const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const LETTER_PITCHES = [0, 2, 4, 5, 7, 9, 11];
// Sharps are added in this order of letters, flats in the reverse
const SHARP_ORDER = [3, 0, 4, 1, 5, 2, 6]; // F C G D A E B
const SHARP = '♯';
const FLAT = '♭';
// Notes outside the key: sharps in sharp keys, flats in flat keys, and in C
// the usual mix
const SHARPS = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
const FLATS = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];
const NEITHER = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
// The key signature for each major key, by its tonic from C up; the ones
// OSMD's transposition picks
const KEY_BY_TONIC = [0, -5, 2, -3, 4, -1, 6, 1, -4, 3, -2, 5];

const names = new Map();
function namesFor(fifths) {
  if (!names.has(fifths)) {
    const list = [...(fifths > 0 ? SHARPS : fifths < 0 ? FLATS : NEITHER)];
    // The key's own notes keep the key's spelling (E♯, C♭ and all)
    const altered = new Array(7).fill(0);
    const order = fifths > 0 ? SHARP_ORDER : [...SHARP_ORDER].reverse();
    for (let i = 0; i < Math.min(7, Math.abs(fifths)); i++) altered[order[i]] = Math.sign(fifths);
    LETTERS.forEach((letter, i) => {
      const pitch = (LETTER_PITCHES[i] + altered[i] + 12) % 12;
      list[pitch] = letter + (altered[i] > 0 ? SHARP : altered[i] < 0 ? FLAT : '');
    });
    names.set(fifths, list);
  }
  return names.get(fifths);
}

/** The key signature a key moves to when transposed by `steps` half steps. */
export function transposedKey(fifths, steps) {
  if (!steps) return fifths;
  const tonic = ((fifths * 7) % 12 + 12) % 12;
  return KEY_BY_TONIC[(((tonic + steps) % 12) + 12) % 12];
}

/** The pitch class of a MIDI note (rounded), spelled for the key. */
export function pitchName(midi, fifths = 0) {
  return namesFor(Math.max(-7, Math.min(7, Math.round(fifths) || 0)))[((Math.round(midi) % 12) + 12) % 12];
}

/** A MIDI note (rounded) spelled for the key, with its octave (C4 is middle C). */
export function noteName(midi, fifths = 0) {
  const name = pitchName(midi, fifths);
  let octave = Math.floor(Math.round(midi) / 12) - 1;
  // B♯ belongs to the octave below its pitch, C♭ to the one above
  if (name === `B${SHARP}`) octave--;
  if (name === `C${FLAT}`) octave++;
  return `${name}${octave}`;
}

/**
 * The key signature shown where an OSMD note is, after transposing by
 * `steps`: the last key change on its staff at or before its bar.
 */
export function keyAtNote(osmd, note, steps = 0) {
  const sheet = osmd?.Sheet;
  const measures = sheet?.SourceMeasures;
  if (!measures || !note?.SourceMeasure) return 0;
  const staff = Math.max(0, sheet.Staves?.indexOf(note.ParentStaff) ?? 0);
  for (let i = measures.indexOf(note.SourceMeasure); i >= 0; i--) {
    const key = measures[i].getKeyInstruction?.(staff);
    if (key) return transposedKey(Number.isFinite(key.keyTypeOriginal) ? key.keyTypeOriginal : key.Key, steps);
  }
  return 0;
}
